import { useFieldContext } from '@/lib/form-contexts'
import withSuspense from '@/hooks/withSuspense'
import {
  designCollectionsFilters,
  useCollections,
} from '@/lib/services/collections/collections.query'
import { cn } from '@/lib/utils'
import {
  closestCorners,
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { useMemo, useRef, useState } from 'react'
import NavItem from './NavItem'
import { Typography } from '@/components/ui/typography'
import {
  COLLECTION_STOREFRONT_VISIBILITY_ISSUE,
  getCollectionStorefrontVisibilityIssue,
} from './designForm.utils'
import { applyNavItemSortOrder, reorderNavItemsByDrag } from './navItemsForm.utils'

function DroppableLane({ id, children, className, laneOfId }) {
  const { setNodeRef } = useDroppable({ id })
  const { active, over } = useDndContext()

  // Only highlight when dragging into THIS lane from the OTHER lane.
  const fromLane = laneOfId?.(active?.id)
  const overLane = laneOfId?.(over?.id)
  const thisLane = laneOfId?.(id)

  const shouldHighlight =
    !!active &&
    !!over &&
    !!fromLane &&
    !!overLane &&
    !!thisLane &&
    overLane === thisLane &&
    fromLane !== overLane

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-14 rounded-md border p-2 flex items-center gap-2',
        shouldHighlight && 'ring-2 ring-ring',
        className
      )}
    >
      {children}
    </div>
  )
}

const NavItemsForm = ({ layout, canDelete, canEdit, showLayoutLabel = false }) => {
  const field = useFieldContext()
  const { data: collections = [] } = useCollections({ filters: designCollectionsFilters })
  const collectionsById = new Map(
    collections.map((collection) => [Number(collection?.id), collection])
  )
  const getNavItemCollectionVisibilityIssue = (item) => {
    const collectionId = Number(item?.collection_id)

    if (item?.type !== 'collection' || !Number.isFinite(collectionId)) {
      return null
    }

    const collection = collectionsById.get(collectionId)

    return collection
      ? getCollectionStorefrontVisibilityIssue(collection)
      : COLLECTION_STOREFRONT_VISIBILITY_ISSUE.HIDDEN
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  )

  const [activeId, setActiveId] = useState(null)
  const lastOverItemIdRef = useRef(null)

  const itemsById = useMemo(() => {
    const map = new Map()
    ;(field.state.value || []).forEach((x) => map.set(x.id, x))
    return map
  }, [field.state.value])

  const activeItem = activeId != null ? itemsById.get(activeId) : null

  const handleDragEndNavItems =
    (field, { leftContainerId = 'left', rightContainerId = 'right' } = {}) =>
    ({ active, over }) => {
      if (!over) return

      const items = field.state.value || []
      const activeId = active.id
      const overId = over.id

      const ALIGN = { left: 'left', right: 'right' }

      const getAlign = (item) => (item?.props?.align === ALIGN.right ? ALIGN.right : ALIGN.left)
      const setAlign = (item, align) => ({
        ...item,
        props: { ...(item.props || {}), align },
      })

      const left = items.filter((x) => getAlign(x) === ALIGN.left).map((x) => x.id)
      const right = items.filter((x) => getAlign(x) === ALIGN.right).map((x) => x.id)

      const isContainer = (id) => id === leftContainerId || id === rightContainerId

      const laneOf = (id) => {
        if (id === leftContainerId) return ALIGN.left
        if (id === rightContainerId) return ALIGN.right
        if (left.includes(id)) return ALIGN.left
        if (right.includes(id)) return ALIGN.right
        return null
      }

      const from = laneOf(activeId)
      const to = laneOf(overId)
      if (!from || !to) return

      // Same-lane + container drop: no-op (we try to avoid this via onDragEnd substitution below)
      if (from === to && isContainer(overId)) return

      const commit = (nextLeftIds, nextRightIds) => {
        const byId = new Map(items.map((x) => [x.id, x]))
        const orderedIds = [...nextLeftIds, ...nextRightIds]

        const next = orderedIds
          .map((id, idx) => {
            const item = byId.get(id)
            if (!item) return null
            const align = nextLeftIds.includes(id) ? ALIGN.left : ALIGN.right
            return {
              ...setAlign(item, align),
              sort_order: idx,
            }
          })
          .filter(Boolean)

        field.setValue(() => applyNavItemSortOrder(next))
      }

      // 1) SAME-LANE REORDER (only when over is an item)
      if (from === to && !isContainer(overId)) {
        const lane = from === ALIGN.left ? left : right
        const oldIndex = lane.indexOf(activeId)
        const newIndex = lane.indexOf(overId)
        if (oldIndex === -1 || newIndex === -1) return

        const moved = arrayMove(lane, oldIndex, newIndex)
        if (from === ALIGN.left) commit(moved, right)
        else commit(left, moved)
        return
      }

      // 2) CROSS-LANE MOVE (or dropped on container)
      let nextLeft = left.filter((id) => id !== activeId)
      let nextRight = right.filter((id) => id !== activeId)

      const dest = to === ALIGN.left ? nextLeft : nextRight
      const overIndex = dest.indexOf(overId)

      // When moving to the left container, insert at the end
      // When moving to the right container, insert at the beginning
      // Feels more natural that way
      const insertAt = isContainer(overId)
        ? overId === leftContainerId
          ? dest.length
          : 0
        : overIndex
      if (insertAt === -1) dest.push(activeId)
      else dest.splice(insertAt, 0, activeId)

      if (to === ALIGN.left) nextLeft = dest
      else nextRight = dest

      commit(nextLeft, nextRight)
    }

  // SPLIT LAYOUT
  if (layout === 'split') {
    const leftLaneId = 'left'
    const rightLaneId = 'right'

    const leftIds =
      field.state.value?.filter((x) => (x?.props?.align ?? 'left') === 'left').map((x) => x.id) ??
      []

    const rightIds =
      field.state.value?.filter((x) => x?.props?.align === 'right').map((x) => x.id) ?? []

    const isLaneId = (id) => id === leftLaneId || id === rightLaneId

    const laneOfId = (id) => {
      if (id === leftLaneId) return 'left'
      if (id === rightLaneId) return 'right'
      if (leftIds.includes(id)) return 'left'
      if (rightIds.includes(id)) return 'right'
      return null
    }

    // Real collisions only; prefer items over lane containers
    const collisionDetection = (args) => {
      const pointerCollisions = pointerWithin(args)
      const collisions = pointerCollisions.length ? pointerCollisions : rectIntersection(args)
      if (!collisions.length) return collisions

      const itemCollisions = collisions.filter((c) => !isLaneId(c.id))
      return itemCollisions.length ? itemCollisions : collisions
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={({ active }) => {
          setActiveId(active?.id)
          lastOverItemIdRef.current = null
        }}
        onDragOver={({ over }) => {
          if (over && !isLaneId(over.id)) {
            lastOverItemIdRef.current = over.id
          }
        }}
        onDragCancel={() => {
          setActiveId(null)
          lastOverItemIdRef.current = null
        }}
        onDragEnd={(evt) => {
          setActiveId(null)

          // If we dropped on a lane container while staying in the same lane,
          // substitute the last item we were over so it reorders instead of appending/no-op.
          let effectiveOver = evt.over

          if (evt.over && isLaneId(evt.over.id) && lastOverItemIdRef.current != null) {
            const fromLane = laneOfId(evt.active?.id)
            const toLane = laneOfId(evt.over.id)
            if (fromLane && toLane && fromLane === toLane) {
              effectiveOver = { ...evt.over, id: lastOverItemIdRef.current }
            }
          }

          lastOverItemIdRef.current = null

          handleDragEndNavItems(field, {
            leftContainerId: leftLaneId,
            rightContainerId: rightLaneId,
          })({
            active: evt.active,
            over: effectiveOver,
          })
        }}
      >
        <div className="@container/nav-items">
          <div
            className="grid grid-cols-1 gap-4 @[44rem]/nav-items:grid-cols-2"
            data-nav-items-form
            data-nav-layout={layout}
          >
            <div>
              {showLayoutLabel && (
                <Typography variant="supporting" className="mb-2 ml-1 block">
                  LEFT
                </Typography>
              )}
              <DroppableLane id={leftLaneId} laneOfId={laneOfId}>
                <SortableContext items={leftIds}>
                  <ul className="flex items-center gap-2 flex-wrap">
                    {leftIds.map((id) => (
                      <NavItem
                        key={id}
                        id={id}
                        canDelete={canDelete}
                        canEdit={canEdit}
                        collectionVisibilityIssue={getNavItemCollectionVisibilityIssue(
                          itemsById.get(id)
                        )}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DroppableLane>
            </div>

            <div>
              {showLayoutLabel && (
                <Typography variant="supporting" align="right" className="mb-2 mr-1 block">
                  RIGHT
                </Typography>
              )}
              <DroppableLane id={rightLaneId} laneOfId={laneOfId} className="justify-end">
                <SortableContext items={rightIds}>
                  <ul className="flex items-center gap-2 flex-wrap justify-end w-full">
                    {rightIds.map((id) => (
                      <NavItem
                        key={id}
                        id={id}
                        canDelete={canDelete}
                        canEdit={canEdit}
                        collectionVisibilityIssue={getNavItemCollectionVisibilityIssue(
                          itemsById.get(id)
                        )}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DroppableLane>
            </div>
          </div>
        </div>

        <DragOverlay adjustScale={false}>
          {!!activeItem && (
            <NavItem
              id={activeItem?.id}
              isOverlay
              canDelete={canDelete}
              canEdit={canEdit}
              collectionVisibilityIssue={getNavItemCollectionVisibilityIssue(activeItem)}
            />
          )}
        </DragOverlay>
      </DndContext>
    )
  }

  // NON-SPLIT
  const justify = {
    left: 'justify-start',
    right: 'justify-end',
    center: 'justify-center',
    spread: 'justify-around',
  }[layout]

  const align = {
    left: 'items-start',
    right: 'items-end',
    center: 'items-center',
    spread: 'items-center',
  }[layout]

  return (
    <div className={cn('flex flex-col gap-2', align)} data-nav-items-form data-nav-layout={layout}>
      {showLayoutLabel && (
        <Typography variant="supporting" className={'block'}>
          {layout?.toUpperCase()}
        </Typography>
      )}
      <div className="min-h-14 rounded-md border p-2 flex items-center gap-2 w-full">
        <ul className={cn('flex items-center gap-2 w-full flex-wrap', justify)}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={({ active, over }) => {
              setActiveId(null)

              if (!over) {
                return
              }

              field.setValue((items) => reorderNavItemsByDrag(items, active?.id, over?.id))
            }}
            onDragStart={({ active }) => setActiveId(active?.id)}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext items={(field.state.value || []).map((x) => x.id)}>
              {field.state.value?.map((item) => (
                <NavItem
                  key={item.id}
                  id={item.id}
                  canDelete={canDelete}
                  canEdit={canEdit}
                  collectionVisibilityIssue={getNavItemCollectionVisibilityIssue(item)}
                />
              ))}
            </SortableContext>

            <DragOverlay adjustScale={false}>
              {!!activeItem && (
                <NavItem
                  id={activeItem?.id}
                  isOverlay
                  collectionVisibilityIssue={getNavItemCollectionVisibilityIssue(activeItem)}
                />
              )}
            </DragOverlay>
          </DndContext>
        </ul>
      </div>
    </div>
  )
}

export default withSuspense(NavItemsForm)
